"use client";

import { useState } from "react";
import type { KundaliRequest } from "@ishvaram/api-client";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { NAKSHATRAS } from "./types";
import Button from "@ishvaram/ui/Button";

export type BabyNameGenderFilter = "all" | "male" | "female";
type BabyNamesMode = "birth" | "nakshatra";

interface BabyNamesFormProps {
  loading: boolean;
  error?: string | null;
  onBirthSubmit: (data: KundaliRequest) => void;
  onNakshatraSubmit: (data: { nakshatra: string; pada?: number; birthDate: string }) => void;
  genderFilter: BabyNameGenderFilter;
  onGenderFilterChange: (gender: BabyNameGenderFilter) => void;
}

function BirthModeForm({
  loading,
  error,
  onSubmit,
}: Pick<BabyNamesFormProps, "loading" | "error"> & {
  onSubmit: BabyNamesFormProps["onBirthSubmit"];
}) {
  return (
    <BirthDetailsForm
      onSubmit={onSubmit}
      loading={loading}
      submitLabel="Find Baby Names"
      error={error ?? undefined}
      analyticsToolName="baby-names"
      requireBirthTime
      requireResolvedPlace
      collectName={false}
    />
  );
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: BabyNamesMode;
  onChange: (mode: BabyNamesMode) => void;
}) {
  return (
    <div
      className="mb-6 flex overflow-hidden rounded-lg border-[1.5px] border-border-default"
      role="group"
      aria-label="Baby name search mode"
    >
      {(
        [
          { value: "birth", label: "By Birth Details" },
          { value: "nakshatra", label: "Select Nakshatra" },
        ] as const
      ).map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={mode === option.value}
          className={`flex-1 py-2.5 text-body-small font-semibold transition-all ${
            mode === option.value
              ? "bg-surface-brand-strong text-text-on-brand"
              : "bg-surface-raised text-text-secondary"
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function GenderFilter({
  value,
  onChange,
}: {
  value: BabyNameGenderFilter;
  onChange: (gender: BabyNameGenderFilter) => void;
}) {
  return (
    <div className="mb-4">
      <span className="mb-2 block text-body-small font-semibold text-text-primary">
        Baby Gender
      </span>
      <div className="flex gap-2" role="group" aria-label="Baby gender">
        {(
          [
            { value: "all", label: "All" },
            { value: "male", label: "Boy" },
            { value: "female", label: "Girl" },
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            className={`flex-1 rounded-lg border-[1.5px] py-2 text-body-small font-semibold transition-all ${
              value === option.value
                ? "border-border-interactive bg-surface-brand-strong text-text-on-brand"
                : "border-border-default bg-surface-raised text-text-secondary"
            }`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BabyNamesForm({
  loading,
  error,
  onBirthSubmit,
  onNakshatraSubmit,
  genderFilter,
  onGenderFilterChange,
}: BabyNamesFormProps) {
  const [mode, setMode] = useState<BabyNamesMode>("birth");
  const [selectedNakshatra, setSelectedNakshatra] = useState("");
  const [selectedPada, setSelectedPada] = useState<string>("");
  const [manualBirthDate, setManualBirthDate] = useState("");

  const handleNakshatraSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNakshatraSubmit({
      nakshatra: selectedNakshatra,
      pada: selectedPada ? Number(selectedPada) : undefined,
      birthDate: manualBirthDate,
    });
  };

  return (
    <div className="mx-auto max-w-md" data-design-owner="baby-names-form">
      <ModeToggle mode={mode} onChange={setMode} />
      <GenderFilter value={genderFilter} onChange={onGenderFilterChange} />

      {mode === "birth" ? (
        <BirthModeForm loading={loading} error={error} onSubmit={onBirthSubmit} />
      ) : (
        <form onSubmit={handleNakshatraSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
          {/* Nakshatra Dropdown */}
          <div>
            <label
              htmlFor="baby-names-nakshatra"
              className="mb-2 block text-body-small font-semibold text-text-primary"
            >
              Select Nakshatra
            </label>
            <select
              id="baby-names-nakshatra"
              required
              value={selectedNakshatra}
              onChange={(e) => setSelectedNakshatra(e.target.value)}
              className="input-sacred w-full rounded-lg px-4 py-3 text-body-small text-text-primary"
            >
              <option value="">Choose nakshatra...</option>
              {NAKSHATRAS.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>
          </div>

          {/* Pada */}
          <div>
            <span className="mb-2 block text-body-small font-semibold text-text-primary">
              Pada <span className="font-normal text-text-muted">(optional)</span>
            </span>
            <div className="flex gap-2" role="group" aria-label="Nakshatra pada">
              {["", "1", "2", "3", "4"].map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={selectedPada === p}
                  className={`flex-1 rounded-lg border-[1.5px] py-2 text-body-small font-semibold transition-all ${
                    selectedPada === p
                      ? "border-border-interactive bg-surface-brand-strong text-text-on-brand"
                      : "border-border-default bg-surface-raised text-text-secondary"
                  }`}
                  onClick={() => setSelectedPada(p)}
                >
                  {p || "All"}
                </button>
              ))}
            </div>
          </div>

          {/* Birth Date (for numerology) */}
          <div>
            <label
              htmlFor="baby-names-birth-date"
              className="mb-2 block text-body-small font-semibold text-text-primary"
            >
              Baby&apos;s Birth Date{" "}
              <span className="font-normal text-text-muted">(for numerology scoring)</span>
            </label>
            <input
              id="baby-names-birth-date"
              type="date"
              required
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
              value={manualBirthDate}
              onChange={(e) => setManualBirthDate(e.target.value)}
              className="input-sacred w-full rounded-lg px-4 py-3 text-body-small text-text-primary"
            />
          </div>

          {/* Error */}
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-[var(--intent-error-border)] bg-[var(--intent-error-surface)] p-3.5 text-body-small text-[var(--intent-error-text)]"
            >
              {error}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            onPress={() => {}}
            loading={loading}
            title="Find Baby Names →"
            className="w-full rounded-lg py-3.5 text-button font-semibold"
          />
        </form>
      )}
    </div>
  );
}
